import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Facebook, ExternalLink, Send, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { isAdminRole } from "@/lib/access";
import { MarkPostedDialog, canMarkPosted, isPosted } from "./MarkPosted";

const READY = ["approved", "scheduled", "published", "unpublished"];

// Group posting status from the existing publishing record (Ad Builder → Publishing → Facebook Group).
export const fbGroupStatus = (c) => (c.facebook_group_status === "published" || isPosted(c) ? "Posted" : READY.includes(c.listing_status) ? "Ready to Post" : "Not Ready");
const TONE = { Posted: "bg-emerald-50 text-emerald-800 border-emerald-200", "Ready to Post": "bg-blue-50 text-blue-800 border-blue-200", "Not Ready": "bg-stone-100 text-stone-600 border-stone-200" };

export function FacebookGroupPost({ c, hid, onLeave, onDone, className = "" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dlg, setDlg] = useState(null);
  const admin = isAdminRole(user);
  const st = fbGroupStatus(c);
  const open = () => {
    const el = document.querySelector('[data-testid="publish-card-facebook_group"]');  // already in this Publishing tab
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    onLeave?.(); navigate(`/horse/${hid}?tab=publishing&section=facebook_group`);
  };
  return (
    <div data-testid="workspace-fb-group" className="mt-2 mb-3 space-y-1.5">
      <Button data-testid="workspace-fb-group-post" onClick={open} className={`w-full bg-[#1877F2] hover:bg-[#166FE5] text-white ${className}`}>
        <Facebook className="h-4 w-4 mr-1.5" />Facebook Group Post
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span data-testid="workspace-fb-group-status" className={`border rounded-full px-2 py-0.5 font-semibold ${TONE[st]}`}>{st}</span>
        {st === "Posted" && c.facebook_url && (
          <a data-testid="workspace-fb-group-open" href={c.facebook_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#1877F2] hover:underline">
            Open Facebook Group Post <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {admin && canMarkPosted(c) && (
        <Button data-testid="workspace-mark-posted" variant="outline" onClick={() => setDlg(true)} className="w-full h-10 border-[#1877F2] text-[#1877F2] hover:bg-blue-50">
          <Send className="h-4 w-4 mr-1.5" />Mark as Posted
        </Button>
      )}
      {admin && isPosted(c) && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span data-testid="workspace-posted" className="font-semibold text-emerald-800">Posted{c.stage === "ongoing" ? " — in Ongoing" : ""}</span>
          <button type="button" data-testid="workspace-correct-posted" onClick={() => setDlg(false)} className="inline-flex items-center gap-1 text-stone-600 hover:underline"><Undo2 className="h-3 w-3" />Correct status</button>
        </div>
      )}
      {dlg !== null && <MarkPostedDialog c={c} posted={dlg} onClose={() => setDlg(null)} onDone={() => { setDlg(null); onDone?.(); }} />}
    </div>
  );
}
